import { CartStoreLineItem } from '../../../lib/adapters/cart-persistence/useCartStore'
import { LocalCartItem } from '../../../lib/adapters/cart-persistence/useLocalCart'
import { formatPrettyTitlePG } from '../../../lib/utils'
import { CartItem } from '../../../types/figs'
import { Variant } from '../../../types/graphql'

export function augmentPersistedCartItemsWithFigsData(
  persistedCartItems: Array<Omit<CartStoreLineItem, 'cost' | 'discounts'> | LocalCartItem>,
  variantsBySku: Record<string, Variant>
): CartItem[] {
  return persistedCartItems.map((item): CartItem => {
    if (isCartStoreLineItem(item)) {
      return augmentCartStoreLineItem(item, variantsBySku)
    } else {
      return augmentLocalCartItem(item, variantsBySku)
    }
  })
}

function isCartStoreLineItem(
  item: Omit<CartStoreLineItem, 'cost' | 'discounts'> | LocalCartItem
): item is Omit<CartStoreLineItem, 'cost' | 'discounts'> {
  return 'cartLineId' in item && 'attributes' in item
}

function augmentCartStoreLineItem(
  item: Omit<CartStoreLineItem, 'cost' | 'discounts'>,
  variantsBySku: Record<string, Variant>
): CartItem {
  const variant = variantsBySku[item.sku!]
  if (!variant) {
    // Cart store data doesn't have enough data overlap, so we create a blank fallback.
    // We should never really fail to find find a variant, but there may be timing issues.
    return {
      image: {
        alt: '',
        url: '',
      },
      fullPrice: 0,
      description: '',
      effectivePrice: 0,
      finalSale: false,
      handle: null,
      key: item.cartLineId,
      cartStoreKey: item.cartLineId,
      productGroupHandle: '',
      productId: '',
      productType: '',
      properties: item.attributes.reduce((accumulator, current) => {
        return Object.assign(accumulator, { [current.key]: current.value })
      }, {}),
      title: '',
      variantId: '',
      sku: item.sku!,
      quantity: item.quantity,
      isLowInventory: false,
    }
  }

  const discountPct = parseFloat(
    item?.attributes.find(e => e.key === '_kitDiscountPct')?.value ?? ''
  )
  const validDiscountPct = !isNaN(discountPct) && discountPct > 0 && discountPct <= 100
  const discountMultiplier = validDiscountPct ? (100 - discountPct) / 100 : 1
  const price = variant.priceDetails.discountPrice?.amount ?? variant.priceDetails.price.amount
  const effectivePrice = Math.round(price * discountMultiplier)

  return {
    key: item.cartLineId,
    cartStoreKey: item.cartLineId,
    sku: item.sku!, // sku should exist, graphql types have it as Maybe<string>
    quantity: item.quantity,
    properties: item.attributes.reduce((accumulator, current) => {
      return Object.assign(accumulator, { [current.key]: current.value })
    }, {}),
    variantId: variant.shopifyId,
    product: variant.product,
    productGroup: variant.productGroup,
    externalParentId: variant.externalParentId,
    priceDetails: variant.priceDetails,
    effectivePrice,
    fit: variant.fit && variant.fit.toLowerCase() !== 'regular' ? variant.fit : '',
    fullPrice: variant.priceDetails.price.amount,
    title: formatPrettyTitlePG(variant.productGroup),
    description: variant.productGroup.description ?? '',
    finalSale: variant.product.finalSale,
    handle: variant.product.handle,
    color: variant.color,
    productGroupHandle: variant.productGroup.handle,
    productId: variant.externalParentId,
    productType: variant.category,
    size: variant.size ?? undefined,
    image: {
      alt: variant.style,
      url: variant.image?.source,
    },
    swPromoEligible: variant.product.swPromoEligible,
    isLowInventory: variant.isLowInventory,
  }
}

function augmentLocalCartItem(
  item: LocalCartItem,
  variantsBySku: Record<string, Variant>
): CartItem {
  const variant = variantsBySku[item.sku]

  if (!variant) return item as CartItem

  const discountPct = parseFloat(item.properties._kitDiscountPct ?? '')
  const validDiscountPct = !isNaN(discountPct) && discountPct > 0 && discountPct <= 100
  const discountMultiplier = validDiscountPct ? (100 - discountPct) / 100 : 1
  const price = variant.priceDetails.discountPrice?.amount ?? variant.priceDetails.price.amount
  const effectivePrice = Math.round(price * discountMultiplier)

  return {
    ...item,
    product: variant.product,
    productGroup: variant.productGroup,
    externalParentId: variant.externalParentId,
    priceDetails: variant.priceDetails,
    effectivePrice,
    fit: variant.fit && variant.fit.toLowerCase() !== 'regular' ? variant.fit : '',
    fullPrice: variant.priceDetails.price.amount,
    title: formatPrettyTitlePG(variant.productGroup),
    description: variant.productGroup.description ?? '',
    finalSale: variant.product.finalSale,
    handle: variant.product.handle,
    color: variant.color,
    productGroupHandle: variant.productGroup.handle,
    productId: variant.externalParentId,
    productType: variant.category,
    size: variant.size ?? undefined,
    image: {
      alt: variant.style,
      url: variant.image?.source,
    },
    isLowInventory: variant.isLowInventory,
  }
}
